/* eslint-disable max-lines */
import { useAddCamera } from '@hakimo-ui/hakimo/data-access';
import { CameraAddPayload } from '@hakimo-ui/hakimo/types';
import { toast, useUser } from '@hakimo-ui/hakimo/util';
import { Alert, Button, InputField, Modal } from '@hakimo-ui/shared/ui-base';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { AddLocationField } from './AddLocationField';
import { ToggleField } from './ToggleField';

interface Props {
  onClose: () => void;
}

export function AddCamera(props: Props) {
  const { tenantId } = useParams<{ tenantId: string }>();
  const { onClose } = props;

  const user = useUser();

  const defaultTenantId = user.tenant.id;

  const [value, setValue] = useState<CameraAddPayload>({
    enableMotionDetection: true,
    enablePlayback: false,
    name: '',
    passwordFilePath: '/secure/rtsp_password',
    userId: '',
    isEnabled: true,
    rtspUrl: '',
    livestreamUrl: '',
    tenantId: tenantId ?? defaultTenantId,
    locationId: '',
  });

  const addCameraMutation = useAddCamera(() => {
    toast(`${value.name} added!`, {
      type: 'success',
    });
    onClose();
  });

  const onSubmit = () => {
    addCameraMutation.mutate(value);
  };

  const hasAllMandatoryValues =
    value.name && value.userId && value.passwordFilePath && value.rtspUrl;

  const actions = (
    <>
      <span className="mr-2">
        <Button
          variant="primary"
          onClick={onSubmit}
          disabled={!hasAllMandatoryValues || addCameraMutation.isLoading}
          loading={addCameraMutation.isLoading}
        >
          Submit
        </Button>
      </span>
      <Button onClick={onClose}>Cancel</Button>
    </>
  );

  return (
    <Modal
      title="Add New Camera"
      footer={actions}
      open={true}
      onClose={onClose}
    >
      <div className="w-[36rem] space-y-4 px-8 py-4 text-sm">
        {addCameraMutation.isError && (
          <Alert type="error">{addCameraMutation.error.message}</Alert>
        )}
        <InputField
          label="Name*"
          type="text"
          value={value.name}
          onChange={(e) =>
            setValue({
              ...value,
              name: e.target.value,
            })
          }
        />
        <InputField
          label="User ID*"
          type="text"
          value={value.userId}
          onChange={(e) =>
            setValue({
              ...value,
              userId: e.target.value,
            })
          }
        />
        <InputField
          label="Password File Path*"
          type="text"
          value={value.passwordFilePath}
          onChange={(e) =>
            setValue({
              ...value,
              passwordFilePath: e.target.value,
            })
          }
        />
        <InputField
          label="RTSP URL*"
          type="text"
          value={value.rtspUrl}
          onChange={(e) =>
            setValue({
              ...value,
              rtspUrl: e.target.value,
            })
          }
        />
        <InputField
          label="Livestream URL"
          type="text"
          value={value.livestreamUrl}
          onChange={(e) =>
            setValue({
              ...value,
              livestreamUrl: e.target.value,
            })
          }
        />
        <AddLocationField
          value={value.locationId}
          onChange={(id) =>
            setValue({
              ...value,
              locationId: id,
            })
          }
        />
        <ToggleField
          label="Motion Detection"
          value={value.enableMotionDetection}
          onChange={(val) =>
            setValue({
              ...value,
              enableMotionDetection: val,
            })
          }
        />
        <ToggleField
          label="Playback"
          value={value.enablePlayback}
          onChange={(val) =>
            setValue({
              ...value,
              enablePlayback: val,
            })
          }
        />
      </div>
    </Modal>
  );
}
